import {
  AddToShoppingCartSource,
  HitProps,
  ISearchProduct,
} from '@monofront/types';
import ProductCard, {
  ProductBadges,
  ProductImage,
  ProductPrice,
  ProductAddToCart,
  ProductFormatAndUnitPrice,
  ProductTitle,
  ProductVendor,
  HiddenPrice,
  ProductOutOfStock,
  ProductComeBackSoon,
  ProductPictograms,
  ProductBundle,
  ProductPromotionalOffer,
  ProductReviews,
  ProductFormat,
} from '@monofront/design-system/components/ProductCard';
import { createAddToShoppingCartInsight } from '@services/productInsights';
import { useNotifyMe } from '@monofront/business/hooks';
import { sendMessageToMobile } from '@utils/mobile';
import React, { useCallback, useMemo } from 'react';
import { stopEventPropagation } from '@utils/stopEventPropagation';
import useProductToShoppingCart from '@hooks/cart/useProductToShoppingCart';
import ButtonAddToWishlist from '@components/ButtonAddToWishlist';
import { useAuthentication } from '@context/businessContainer';
import {
  getHasHiddenPrice,
  getIsAntiWaste,
  getMinimumProductQuantity,
} from '@monofront/business/utils';
import { toast } from 'react-toastify';
import { useTranslations } from 'next-intl';
import { isMobileApp } from '@utils/environment';
import {
  ABTEST_STROKE_PRICE,
  ALGOLIA_INSIGHTS_EVENT_NAME,
} from '@monofront/business/constants';
import {
  useEditorialSidePanelContext,
  useFeatureIsOn,
} from '@monofront/business/contexts';

type HitComponentProps = HitProps<ISearchProduct> & {
  onClick?: () => void;
  trackProductInteraction?: () => void;
};

const Hit: React.FC<HitComponentProps> = ({
  hit,
  sendEvent,
  onClick,
  trackProductInteraction,
}) => {
  const { isAuthenticated, authUser } = useAuthentication();
  const { loading, notifyMe } = useNotifyMe();
  const tCatalog = useTranslations('Catalog');
  const isAntiWaste = getIsAntiWaste(hit.tags);
  const addToShoppingCartInsight = useMemo(
    () =>
      createAddToShoppingCartInsight({
        sendEvent,
        hit,
      }),
    [sendEvent, hit],
  );
  const {
    localProductQuantity,
    currentProductQuantity,
    incrementCount,
    decrementCount,
  } = useProductToShoppingCart(
    hit,
    { source: AddToShoppingCartSource.DEFAULT },
    addToShoppingCartInsight,
  );
  const { setEditorialPanelSourceUid, setEditorialSidePanelProps } =
    useEditorialSidePanelContext();
  const isABtestEnabled = useFeatureIsOn(ABTEST_STROKE_PRICE);

  const handleClickItem = useCallback(
    (event: React.SyntheticEvent) => {
      sendEvent(
        'click',
        hit,
        ALGOLIA_INSIGHTS_EVENT_NAME.SEARCH_RESULT_CLICKED,
      );

      sessionStorage.setItem('searchScrollY', window.scrollY.toString());
      if (isMobileApp) {
        sendMessageToMobile('goToProduct', { handle: hit.handle });
        stopEventPropagation(event);
      }

      onClick?.();
      trackProductInteraction?.();
    },
    [sendEvent, isMobileApp, onClick],
  );

  const handleNotifyMe = useCallback(
    async (
      email: string,
      handleSuccess?: () => void,
      handleError?: (errorMessageKey: 'notifyMeError') => void,
    ): Promise<void> => {
      await notifyMe({
        email,
        sku: hit.sku,
        successCallback: (messageKey) => {
          toast(tCatalog(messageKey));
          handleSuccess?.();
        },
        errorCallback: (errorMessageKey) => {
          toast(tCatalog(errorMessageKey), {
            progressClassName: 'toast-error',
          });
          handleError?.(errorMessageKey);
        },
      });
    },
    [notifyMe, hit.sku],
  );

  const handleAddToCart = () => {
    incrementCount();
    trackProductInteraction?.();
  };

  const shouldHidePrice = Boolean(!isAuthenticated && getHasHiddenPrice(hit));
  const hasEnoughStock =
    hit.inventory_quantity >= getMinimumProductQuantity(hit.tags);

  hit.inventory_available = hasEnoughStock;

  const onTooltipClick = isABtestEnabled
    ? () => {
        setEditorialPanelSourceUid(
          isAntiWaste ? 'stroke-price-anti-waste' : 'stroke-price',
        );
        setEditorialSidePanelProps({ product: hit });
      }
    : undefined;

  return (
    <ProductCard product={hit} isInCart={currentProductQuantity > 0}>
      <ProductImage />
      <ProductPictograms />
      <ProductBadges />
      {shouldHidePrice ? (
        <HiddenPrice />
      ) : (
        <ProductPrice
          onTooltipClick={onTooltipClick}
          quantity={localProductQuantity ?? 0}
        />
      )}
      <ProductBundle />
      <ProductPromotionalOffer quantity={localProductQuantity ?? 0} />
      {shouldHidePrice ? (
        <ProductFormat />
      ) : (
        <ProductFormatAndUnitPrice quantity={localProductQuantity ?? 0} />
      )}
      <ProductTitle handleClick={handleClickItem} />
      <ButtonAddToWishlist product={hit} />
      <ProductAddToCart
        handleAddToCart={handleAddToCart}
        handleRemoveFromCart={decrementCount}
        quantity={localProductQuantity ?? 0}
      />
      <ProductVendor />
      <ProductReviews />
      {isAntiWaste ? (
        <ProductOutOfStock />
      ) : (
        <ProductComeBackSoon
          userEmail={authUser?.email}
          handleNotifyMe={handleNotifyMe}
          isNotifyMeLoading={loading}
        />
      )}
    </ProductCard>
  );
};

export default Hit;
